import React, { useState } from 'react';
import { CheckCircle, XCircle, MapPin, Calendar, Clock, User, AlertCircle, Home as HomeIcon } from 'lucide-react';

const HomeVisitRequests = () => {
  const [requests, setRequests] = useState([
    {
      id: '1',
      nurse: 'Nurse Mary Johnson',
      date: '2026-01-31',
      time: '10:00 AM',
      purpose: 'Antenatal Home Check',
      notes: 'Patient requested home visit due to mobility issues',
      status: 'pending',
      requestedOn: '2026-01-29',
    },
    {
      id: '2',
      nurse: 'Nurse Sarah Williams',
      date: '2026-02-02',
      time: '02:00 PM',
      purpose: 'Postnatal Follow-up',
      notes: 'Post-delivery check-up',
      status: 'pending',
      requestedOn: '2026-01-29',
    },
    {
      id: '3',
      nurse: 'Nurse Mary Johnson',
      date: '2026-01-28',
      time: '09:00 AM',
      purpose: 'Vitals Check',
      notes: 'Regular monitoring required',
      status: 'approved',
      requestedOn: '2026-01-27',
      address: '15 Ademola Street, Ikeja, Lagos',
    },
  ]);

  const [showAddressModal, setShowAddressModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [address, setAddress] = useState('');

  const handleApprove = (request) => {
    setSelectedRequest(request);
    setShowAddressModal(true);
  };

  const handleSubmitAddress = (e) => {
    e.preventDefault();
    
    if (!address.trim()) {
      alert('Please enter your address');
      return;
    }

    setRequests(prev =>
      prev.map(req =>
        req.id === selectedRequest.id
          ? { ...req, status: 'approved', address }
          : req
      )
    );

    setShowAddressModal(false);
    setAddress('');
    setSelectedRequest(null);
    alert('Home visit approved! The nurse has been notified.');
  };

  const handleReject = (requestId) => {
    if (!window.confirm('Are you sure you want to reject this home visit request?')) {
      return;
    }

    setRequests(prev =>
      prev.map(req =>
        req.id === requestId
          ? { ...req, status: 'rejected' }
          : req
      )
    );

    alert('Home visit request rejected. The nurse has been notified.');
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: <AlertCircle className="w-4 h-4" /> },
      approved: { bg: 'bg-green-100', text: 'text-green-700', icon: <CheckCircle className="w-4 h-4" /> },
      rejected: { bg: 'bg-red-100', text: 'text-red-700', icon: <XCircle className="w-4 h-4" /> },
    };
    return badges[status] || badges.pending;
  };

  const pendingRequests = requests.filter(req => req.status === 'pending');
  const processedRequests = requests.filter(req => req.status !== 'pending');

  return (
    <div className="p-6 md:p-8 min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-2">
            <div className="p-3 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl shadow-lg shadow-emerald-500/30">
              <HomeIcon className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900">
                Home Visit Requests
              </h1>
              <p className="text-gray-600 mt-1">Review and respond to home visit requests</p>
            </div>
          </div>
        </div>

        {/* Pending Requests */}
        {pendingRequests.length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <AlertCircle className="w-6 h-6 text-amber-600" />
              Pending Requests ({pendingRequests.length})
            </h2>
            <div className="space-y-4">
              {pendingRequests.map(request => (
                  <div
                    key={request.id}
                    className="bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-200 rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow"
                  >
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-start gap-4">
                          <div className="p-3 bg-amber-100 rounded-xl">
                            <MapPin className="w-6 h-6 text-amber-600" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="text-lg font-bold text-gray-900">{request.purpose}</h3>
                            </div>
                            <p className="text-gray-600 mb-3 flex items-center gap-2">
                              <User className="w-4 h-4 text-emerald-600" />
                              {request.nurse}
                            </p>
                            
                            <div className="grid md:grid-cols-2 gap-3 mb-3">
                              <div className="flex items-center gap-2 text-sm text-gray-600">
                                <Calendar className="w-4 h-4 text-emerald-600" />
                                <span>{new Date(request.date).toLocaleDateString('en-US', { 
                                  weekday: 'short', 
                                  month: 'short', 
                                  day: 'numeric',
                                  year: 'numeric'
                                })}</span>
                              </div>
                              <div className="flex items-center gap-2 text-sm text-gray-600">
                                <Clock className="w-4 h-4 text-emerald-600" />
                                <span>{request.time}</span>
                              </div>
                            </div>
                            
                            {request.notes && (
                              <div className="p-3 bg-white rounded-lg border border-amber-200">
                                <p className="text-sm text-gray-700"><strong>Notes:</strong> {request.notes}</p>
                              </div>
                            )}

                            <div className="mt-3 text-xs text-gray-500">
                              Requested on: {new Date(request.requestedOn).toLocaleDateString()}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="flex gap-2 lg:flex-col">
                        <button
                          onClick={() => handleApprove(request)}
                          className="flex-1 lg:flex-none px-6 py-3 bg-green-600 text-white rounded-xl font-semibold hover:bg-green-700 transition-all flex items-center justify-center gap-2 shadow-lg"
                        >
                          <CheckCircle className="w-5 h-5" />
                          Approve
                        </button>
                        <button
                          onClick={() => handleReject(request.id)}
                          className="flex-1 lg:flex-none px-6 py-3 bg-red-600 text-white rounded-xl font-semibold hover:bg-red-700 transition-all flex items-center justify-center gap-2 shadow-lg"
                        >
                          <XCircle className="w-5 h-5" />
                          Reject
                        </button>
                      </div>
                    </div>
                  </div>
              ))}
            </div>
          </div>
        )}

        {/* Processed Requests */}
        {processedRequests.length > 0 && (
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <CheckCircle className="w-6 h-6 text-gray-600" />
              Previous Requests
            </h2>
            <div className="space-y-4">
              {processedRequests.map(request => {
                const statusBadge = getStatusBadge(request.status);
                return (
                  <div
                    key={request.id}
                    className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-shadow"
                  >
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-start gap-4">
                          <div className="p-3 bg-gray-50 rounded-xl">
                            <MapPin className="w-6 h-6 text-gray-600" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="text-lg font-bold text-gray-900">{request.purpose}</h3>
                              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${statusBadge.bg} ${statusBadge.text} flex items-center gap-1`}>
                                {statusBadge.icon}
                                {request.status}
                              </span>
                            </div>
                            <p className="text-gray-600 mb-3 flex items-center gap-2">
                              <User className="w-4 h-4 text-emerald-600" />
                              {request.nurse}
                            </p>
                            
                            <div className="grid md:grid-cols-2 gap-3">
                              <div className="flex items-center gap-2 text-sm text-gray-600">
                                <Calendar className="w-4 h-4 text-emerald-600" />
                                <span>{new Date(request.date).toLocaleDateString('en-US', { 
                                  weekday: 'short', 
                                  month: 'short', 
                                  day: 'numeric',
                                  year: 'numeric'
                                })}</span>
                              </div>
                              <div className="flex items-center gap-2 text-sm text-gray-600">
                                <Clock className="w-4 h-4 text-emerald-600" />
                                <span>{request.time}</span>
                              </div>
                              {request.address && (
                                <div className="flex items-center gap-2 text-sm text-gray-600 md:col-span-2">
                                  <MapPin className="w-4 h-4 text-emerald-600" />
                                  <span>{request.address}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* No Requests */}
        {requests.length === 0 && (
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-12 text-center">
            <HomeIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">No home visit requests</h3>
            <p className="text-gray-600">You don't have any home visit requests at the moment</p>
          </div>
        )}

        {/* Address Modal */}
        {showAddressModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-2xl font-bold text-gray-900">Provide Your Address</h2>
                <p className="text-gray-600 mt-1">Please enter your address for the home visit</p>
              </div>

              <form onSubmit={handleSubmitAddress} className="p-6 space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Home Address *
                  </label>
                  <textarea
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    required
                    rows="4"
                    placeholder="Enter your complete address including street, area, city..."
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none"
                  />
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <p className="text-sm text-blue-800">
                    <strong>Visit Details:</strong><br />
                    Date: {selectedRequest && new Date(selectedRequest.date).toLocaleDateString()}<br />
                    Time: {selectedRequest?.time}<br />
                    Purpose: {selectedRequest?.purpose}
                  </p>
                </div>

                <div className="flex gap-4">
                  <button
                    type="submit"
                    className="flex-1 px-6 py-3 bg-gradient-to-r from-emerald-600 to-emerald-500 text-white rounded-xl font-semibold hover:from-emerald-700 hover:to-emerald-600 transition-all shadow-lg shadow-emerald-500/30"
                  >
                    Confirm & Approve
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddressModal(false);
                      setAddress('');
                      setSelectedRequest(null);
                    }}
                    className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-all"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HomeVisitRequests;
